import { Chat } from '@mui/icons-material';
import { Alert, Box, Collapse, LinearProgress, List, Stack, Typography, useTheme } from '@mui/material';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import * as React from 'react';
import { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { CommentDTO, CommentUpdateDTO, TaskType } from '../../api/dto';
import { addComment, getDocumentComments, TaskPreciseDTO } from '../../api/task';
import { CreateOne } from '../../api/types';
import { DefaultAccordion } from '../DefaultAccordion';
import { DocumentDetails } from '../tasks/useTasks';
import { CommentInput } from './CommentInput';
import { CommentListItem } from './CommentListItem';

interface CommentListProps {
    documentDetails: DocumentDetails[];
    currentTask: TaskPreciseDTO | null;
    disabled: boolean;
    addCommentCallback?: CreateOne<{ data: CommentUpdateDTO; id: string }, CommentDTO>;
    isEditableWithoutTasks?: boolean;
    collapsed?: (comments: CommentDTO[] | undefined) => boolean;
}
export const CommentList: React.FC<CommentListProps> = ({
    documentDetails,
    isEditableWithoutTasks,
    currentTask,
    addCommentCallback,
    disabled,
    collapsed,
}) => {
    const intl = useIntl();
    const theme = useTheme();
    const queryClient = useQueryClient();
    const [text, setText] = useState('');
    const {
        isLoading,
        isError,
        data: commentsData,
    } = useQuery(
        ['comments', JSON.stringify(documentDetails)],
        () => {
            return getDocumentComments({ data: documentDetails });
        },
        {
            onSuccess: () => setText(''),
            onError: () => setText(''),
        }
    );
    const reloadComments = async () => {
        await queryClient.invalidateQueries(['comments', JSON.stringify(documentDetails)]);
    };
    const isCurrentTaskAddComment = !!currentTask && currentTask.type.type === TaskType.ADD_COMMENT;
    const commentTaskId = isCurrentTaskAddComment ? currentTask.id : null;

    // Trick to enable the transitions when comments are added
    const [commentsLength, setCommentsLength] = useState(Number.MAX_SAFE_INTEGER);
    useEffect(() => commentsData && setCommentsLength(commentsData.length), [commentsData]);

    const commentCount = commentsData?.length;

    return (
        <Box sx={{ mt: 2 }}>
            <DefaultAccordion
                title={intl.formatMessage({ id: 'document.panel.comments' })}
                icon={<Chat color="action" />}
                disableGutters
                collapsed={isCurrentTaskAddComment || !collapsed ? false : collapsed(commentsData)}
                chipValue={!isLoading && commentCount ? commentCount : undefined}
            >
                {isLoading && <LinearProgress />}
                <Stack direction="column" spacing={0}>
                    {isError ? (
                        <Box sx={{ p: 2 }}>
                            <Alert severity="error">
                                <FormattedMessage id="common.error" />
                            </Alert>
                        </Box>
                    ) : isLoading ? (
                        <Box sx={{ textAlign: 'center', p: 2 }}>
                            <Typography sx={{ m: 2, mb: 0 }}>
                                <FormattedMessage id="document.comment.loading" />
                            </Typography>
                        </Box>
                    ) : !commentsData || commentsData.length < 1 ? (
                        commentTaskId === null ? (
                            <Box sx={{ p: 2 }}>
                                <Alert severity="info">
                                    <FormattedMessage id="document.comment.alertEmpty" />
                                </Alert>
                            </Box>
                        ) : null
                    ) : (
                        <List sx={{ p: 0 }}>
                            {commentsData.map((comment, idx) => (
                                <Collapse key={comment.id} in={idx < commentsLength}>
                                    <CommentListItem
                                        comment={comment}
                                        reloadComments={reloadComments}
                                        sx={{
                                            p: 2,
                                            backgroundColor: idx % 2 !== 0 ? theme.palette.grey['100'] : 'white',
                                        }}
                                    />
                                </Collapse>
                            ))}
                        </List>
                    )}
                    {(isEditableWithoutTasks || commentTaskId !== null) && (
                        <Box sx={{ p: 2 }}>
                            <CommentInput
                                addComment={commentTaskId ? addComment : addCommentCallback!}
                                id={commentTaskId ?? documentDetails[0].documentId.toString()}
                                onSubmit={reloadComments}
                                text={text}
                                setText={setText}
                                disabled={isLoading || disabled}
                            />
                        </Box>
                    )}
                </Stack>
            </DefaultAccordion>
        </Box>
    );
};
